import React, { useMemo } from 'react';
import BlockChip from './BlockChip';
import {
  DayHeader,
  DropPreview,
  Ghost,
  HourGutter,
  NowLine,
  TimedBlock,
  gridBackground,
  hourRange,
  timedTitle,
  useCreateDrag,
  useMoveDrag,
  useNowMin,
} from './timeGrid';
import { BLOCK_TYPES, initials, isWeekend, layoutDay, todayISO } from './scheduleUtils';

const COLS = '56px repeat(7, minmax(116px, 1fr))';

/**
 * Timeline — a shared week calendar with every visible doctor overlaid in
 * their own colour (Google-Calendar style). Timed sessions sit on the hour
 * grid; on-call and leave sit in the All day row so they never bury the lists.
 * Click a day heading to drill into that day.
 */
const TimelineView = ({ days, doctors, blocks, hospitalsById, colours, conflicts, canEdit, canCreate, onAdd, onEdit, onMove, onOpenDay }) => {
  const today = todayISO();
  const doctorsById = useMemo(() => Object.fromEntries(doctors.map((d) => [d.uid, d])), [doctors]);
  const timed = blocks.filter((b) => BLOCK_TYPES[b.btype].timed);
  const allDay = blocks.filter((b) => !BLOCK_TYPES[b.btype].timed);
  const range = hourRange(timed);
  const nowMin = useNowMin();
  const create = useCreateDrag(range, (bdate, bstart, bend) => onAdd({ bdate, bstart, bend }));
  const move = useMoveDrag(range, (block, bdate, bstart, bend, copy) => onMove(block, { bdate, bstart, bend }, copy));

  return (
    <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="min-w-[868px]">
        {/* Sticky header: day names + All day row */}
        <div className="sticky top-0 z-30 border-b border-slate-200 bg-white">
          <div className="grid" style={{ gridTemplateColumns: COLS }}>
            <div />
            {days.map((day) => (
              <DayHeader key={day} day={day} today={today} onOpen={onOpenDay} className="border-l border-slate-100" />
            ))}

            <div className="border-t border-slate-100 px-1 py-2 text-right text-[10px] font-semibold uppercase leading-3 text-slate-400">All day</div>
            {days.map((day) => (
              <div key={day} className={`flex max-h-[148px] min-h-[36px] flex-col gap-1 overflow-y-auto border-l border-t border-slate-100 p-1 ${isWeekend(day) ? 'bg-slate-50' : ''}`}>
                {allDay
                  .filter((b) => b.bdate === day)
                  .map((b) => (
                    <BlockChip
                      key={b.bid}
                      block={b}
                      hospital={hospitalsById[b.bhid]}
                      colour={colours[b.buid]}
                      conflict={conflicts.get(b.bid)}
                      doctorLabel={initials(doctorsById[b.buid])}
                      draggable={false}
                      onClick={() => onEdit(b)}
                    />
                  ))}
              </div>
            ))}
          </div>
        </div>

        {/* Hour grid */}
        <div className="grid" style={{ gridTemplateColumns: COLS }}>
          <HourGutter range={range} />
          {days.map((day) => (
            <div
              key={day}
              className={`relative border-l border-slate-100 ${canCreate ? 'cursor-crosshair' : ''}`}
              style={{
                ...gridBackground,
                height: range.height,
                backgroundColor: day === today ? 'rgba(48, 207, 192, 0.04)' : isWeekend(day) ? '#f8fafc' : undefined,
              }}
              onMouseDown={(e) => { if (canCreate) create.start(e, day); }}
              {...move.columnProps(day)}
            >
              {layoutDay(timed.filter((b) => b.bdate === day)).map(({ block: b, col, cols }) => (
                <TimedBlock
                  key={b.bid}
                  block={b}
                  col={col}
                  cols={cols}
                  range={range}
                  colour={colours[b.buid]}
                  label={initials(doctorsById[b.buid])}
                  title={timedTitle(b, hospitalsById)}
                  detail={[b.btheatre, b.bsurgeon].filter(Boolean).join(' · ')}
                  notes={b.bnotes}
                  conflict={conflicts.get(b.bid)}
                  dragging={move.draggingBid === b.bid}
                  onClick={() => onEdit(b)}
                  dragProps={move.blockProps(b, canEdit(b.buid))}
                />
              ))}
              {create.ghost?.key === day && <Ghost ghost={create.ghost} range={range} />}
              {move.preview?.key === day && <DropPreview preview={move.preview} range={range} colour={colours[move.preview.block.buid]} />}
              {day === today && <NowLine nowMin={nowMin} range={range} />}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TimelineView;
